import { useAccount } from "wagmi";
import { formatUnits } from "viem";
import {
  Gift,
  TrendingUp,
  Users,
  Wallet,
  Loader2,
} from "lucide-react";
import { RewardPoolCard } from "../components/rewards/RewardPoolCard";
import { useRewards } from "../hooks/useRewards";
import { formatTokenAmount } from "../lib/utils";

/* ═══════════════════════════════════════════════════════════════════════════ */
/* STATS BANNER                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function StatsBanner() {
  const { isConnected } = useAccount();
  const { slots, djnnBalance, isLoading } = useRewards();

  /* Compute the max holder count across all distributors */
  const maxHolders = slots.reduce(
    (max, s) => (s.shareholderCount > max ? s.shareholderCount : max),
    0n
  );

  /* Compute user share % — use slot 0 share info if available */
  const slot0 = slots[0];
  let sharePercent = "0.00";
  if (slot0 && slot0.totalDividends > 0n && djnnBalance > 0n) {
    /* Approximate: user share = pending + realised / total distributed */
    const userTotal = slot0.pendingReward + slot0.totalRealised;
    if (slot0.totalDistributed > 0n) {
      const pct = (Number(userTotal) / Number(slot0.totalDistributed)) * 100;
      sharePercent = pct < 0.01 && pct > 0 ? "<0.01" : pct.toFixed(2);
    }
  }

  return (
    <div className="glass-card p-6 sm:p-8 mb-8 max-w-4xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
        {/* Total Holders */}
        <div className="text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-4 h-4 text-primary" />
            </div>
            <span className="text-sm text-text-tertiary font-medium">
              Total Holders
            </span>
          </div>
          <p className="neon-text-orange font-display text-2xl sm:text-3xl font-bold">
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin inline-block" />
            ) : (
              maxHolders.toLocaleString()
            )}
          </p>
        </div>

        {/* Total Distributed — show first token as representative */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#9FFCE8]/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-secondary" />
            </div>
            <span className="text-sm text-text-tertiary font-medium">
              Reward Tokens
            </span>
          </div>
          <p className="neon-text-orange font-display text-2xl sm:text-3xl font-bold">
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin inline-block" />
            ) : (
              slots.length
            )}
          </p>
        </div>

        {/* Your Share */}
        <div className="text-center sm:text-right">
          <div className="flex items-center justify-center sm:justify-end gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#F7931A]/10 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-[#F7931A]" />
            </div>
            <span className="text-sm text-text-tertiary font-medium">
              Your Share
            </span>
          </div>
          <p className="neon-text-orange font-display text-2xl sm:text-3xl font-bold">
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin inline-block" />
            ) : !isConnected ? (
              "---"
            ) : (
              `${sharePercent}%`
            )}
          </p>
        </div>
      </div>

      {/* Per-token distribution totals */}
      {slots.length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/[0.06]">
          <div className="text-xs text-text-tertiary uppercase tracking-wider font-semibold mb-3">
            Lifetime Distributed
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {slots.map((slot) => {
              const totalFormatted = formatTokenAmount(
                slot.totalDistributed,
                slot.meta.decimals,
                slot.meta.decimals <= 8 ? 4 : 2
              );
              return (
                <div
                  key={slot.slot}
                  className="bg-surface/30 rounded-button p-3 border border-border"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ background: slot.meta.color }}
                    />
                    <span className="text-xs text-text-secondary font-medium">
                      {slot.meta.symbol}
                    </span>
                  </div>
                  <p className="font-display font-bold text-sm text-text-primary">
                    {totalFormatted}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* REWARDS PAGE                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function RewardsPage() {
  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      {/* Background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-60 -right-60 w-[800px] h-[800px] rounded-full bg-primary/[0.03] blur-[150px]" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-secondary/[0.04] blur-[120px]" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#F7931A]/[0.02] blur-[130px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-20">
        {/* ── Page Header ──────────────────────────────────────────── */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 text-sm text-primary font-medium mb-4">
            <Gift className="w-3.5 h-3.5" />
            Holder Rewards
          </div>
          <h1 className="neon-text-orange font-display text-4xl sm:text-5xl font-bold mb-3 animate-neon-pulse">
            DJNN Rewards
          </h1>
          <p className="text-text-secondary text-base sm:text-lg max-w-lg mx-auto">
            Earn rewards simply by holding DJNN tokens
          </p>
        </div>

        {/* ── Stats Banner ─────────────────────────────────────────── */}
        <StatsBanner />

        {/* ── Reward Pool Card ─────────────────────────────────────── */}
        <div className="flex justify-center">
          <RewardPoolCard />
        </div>
      </div>
    </div>
  );
}
